import type { DrawingDocument } from '../model';
import { aromaticBondIds, bondStereoValues, drawnBondOrders } from '../domain/documentSelectors.ts';
import { translateAnnotations } from '../domain/objectAlignment.ts';
import { chosenPage } from '../domain/page.ts';
import { drawingDocumentToSvg } from './svgExport.ts';

export interface ContentExtent {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * #1140 (ADR 0025): the area the drawing's artwork covers, in drawing units -- labels, strokes,
 * arrowheads and text included -- as the renderer itself measures it (Export's "Whole drawing"
 * crops to the same box). Null for an empty drawing.
 */
export function contentExtent(document: DrawingDocument): ContentExtent | null {
  if (document.structure.graph.nodes.length === 0 && document.annotations.length === 0) return null;
  const { width, height } = document.depiction.coordinateSet;
  const svg = drawingDocumentToSvg(
    document,
    {
      bondOrders: drawnBondOrders(document),
      aromaticBondIds: aromaticBondIds(document),
      stereoBonds: bondStereoValues(document),
      overriddenBondIds: new Set()
    },
    '#000000',
    { width, height },
    '',
    {},
    [],
    [],
    { geometry: 'authored', paddingPx: 0, background: null }
  );
  const box = /viewBox="([-\d.]+) ([-\d.]+) ([-\d.]+) ([-\d.]+)"/.exec(svg);
  if (!box) return null;
  const [x, y, boxWidth, boxHeight] = box.slice(1).map(Number) as [number, number, number, number];
  return { x, y, width: boxWidth, height: boxHeight };
}

/** ADR 0025 D7: room kept around a saved drawing's artwork (drawing units). */
export const SAVED_MARGIN = 10;
/** Where a drawing saved without its paper sits when opened: half an inch from the page's corner. */
export const PAGE_MARGIN = 48;

/** `document` with everything on it moved by (dx, dy), in a coordinate space of `size`. */
function moved(document: DrawingDocument, dx: number, dy: number, size: { width: number; height: number }) {
  const { coordinateSet } = document.depiction;
  const shifted: DrawingDocument = {
    ...document,
    depiction: {
      ...document.depiction,
      coordinateSet: {
        ...coordinateSet,
        ...size,
        coordinates: Object.fromEntries(
          Object.entries(coordinateSet.coordinates).map(([id, point]) => [
            id,
            { ...point, x: point.x + dx, y: point.y + dy }
          ])
        )
      }
    }
  };
  return translateAnnotations(shifted, new Set(document.annotations.map((annotation) => annotation.id)), dx, dy);
}

/**
 * ADR 0025 D7 (#1140, #1143): the drawing cut down to its artwork -- the renderer's own measure
 * (contentExtent, as Export's "Whole drawing"), plus a margin -- with everything moved so the
 * artwork starts at that margin. An empty drawing is returned as it is.
 */
export function croppedToContent(document: DrawingDocument, margin = SAVED_MARGIN): DrawingDocument {
  const extent = contentExtent(document);
  if (!extent) return document;
  return moved(document, margin - extent.x, margin - extent.y, {
    width: extent.width + 2 * margin,
    height: extent.height + 2 * margin
  });
}

/**
 * ADR 0025 D7 (#1143, owner): a drawing as it is saved -- its artwork's area, not the paper, and a
 * page only if its author chose one (chosenPage).
 */
export function asSaved(document: DrawingDocument): DrawingDocument {
  const { page, ...rest } = croppedToContent(document);
  const chosen = chosenPage(page);
  return chosen ? { ...rest, page: chosen } : rest;
}

/**
 * #1143: a saved drawing, opened -- its artwork moved in from the page's corner when it was saved
 * cut down to it (it starts within the margin), so it sits on the page as drawn on one. A drawing
 * saved with its whole paper (before #1143) is already where it was, and is left there.
 */
export function placedOnPage(document: DrawingDocument, margin = PAGE_MARGIN): DrawingDocument {
  const extent = contentExtent(document);
  if (!extent) return document;
  const dx = extent.x < margin ? margin - extent.x : 0;
  const dy = extent.y < margin ? margin - extent.y : 0;
  if (dx === 0 && dy === 0) return document;
  const { width, height } = document.depiction.coordinateSet;
  return moved(document, dx, dy, { width: width + dx, height: height + dy });
}
